import { useCallback, useState } from 'react';
import { BookLock } from 'lucide-react';
import { periodReportApi } from '@/api/reportCentre.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { ReportToolbar, RunReportButton } from '@/components/reports/ReportToolbar';
import { exportToExcel, exportToCsvRows } from '@/utils/reportExport';
import type { ExcelColumn } from '@/utils/reportExport';
import {
  INPUT_CLS, LABEL_CLS, TH_CLS, TH_R_CLS, AMT_CLS, CARD_CLS,
  fmtDate, fmtAmtZero, EmptyState,
} from '@/components/analytics/AnalyticsKit';
import {
  Pill, Dash, TabBar, InfoBanner, WarnBanner, errMessage,
} from './ReportCentreKit';
import type { PillTone } from './ReportCentreKit';
import type { YearEndReportDto } from '@/types/reportCentre.types';

const REPORT_KEY = 'YEAR_END';

const STATUS_TONES: Record<string, PillTone> = {
  Completed:  'green',
  Failed:     'red',
  RolledBack: 'amber',
  InProgress: 'blue',
  Pending:    'blue',
  NotRun:     'grey',
};

const hasCfDifference = (diff: number) => Math.abs(diff) >= 0.01;

// ── Export ────────────────────────────────────────────────────────────────────

const MOVEMENT_COLUMNS: ExcelColumn[] = [
  { key: 'Code',      header: 'Account Code',   width: 14 },
  { key: 'Name',      header: 'Account Name',   width: 32 },
  { key: 'Pre',       header: 'Pre-Close',      width: 16, numFmt: '#,##0.00' },
  { key: 'Dr',        header: 'Closing Dr',     width: 16, numFmt: '#,##0.00' },
  { key: 'Cr',        header: 'Closing Cr',     width: 16, numFmt: '#,##0.00' },
  { key: 'Post',      header: 'Post-Close',     width: 16, numFmt: '#,##0.00' },
];

const CARRYFORWARD_COLUMNS: ExcelColumn[] = [
  { key: 'Code',      header: 'Account Code',       width: 14 },
  { key: 'Name',      header: 'Account Name',       width: 32 },
  { key: 'Dr',        header: 'Carried Dr',         width: 16, numFmt: '#,##0.00' },
  { key: 'Cr',        header: 'Carried Cr',         width: 16, numFmt: '#,##0.00' },
  { key: 'Prior',     header: 'Prior-Year Closing', width: 18, numFmt: '#,##0.00' },
  { key: 'Diff',      header: 'Difference',         width: 16, numFmt: '#,##0.00' },
];

// ── Component ─────────────────────────────────────────────────────────────────

export const YearEndReportPage = () => {
  const { hasPermission } = usePermission();

  const [yearCode, setYearCode] = useState('');
  const [tab, setTab] = useState('movements');
  const [data, setData] = useState<YearEndReportDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await periodReportApi.getYearEnd({ yearCode: yearCode.trim() });
      setData(res.data?.data ?? null);
    } catch (e: unknown) {
      setError(errMessage(e));
    } finally {
      setLoading(false);
    }
  }, [yearCode]);

  if (!hasPermission(Permissions.RPT_PERIOD_STATUS)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        You do not have permission to view this report.
      </div>
    );
  }

  const movements = data?.movements ?? [];
  const carryForwards = data?.carryForwards ?? [];
  const isMovementTab = tab === 'movements';
  const cfBroken = carryForwards.some(c => hasCfDifference(c.difference));
  const notRun = data?.status === 'NotRun';

  const handleExcel = () => {
    if (!data) return;
    if (isMovementTab) {
      exportToExcel({
        sheetName: 'Closing Movement',
        columns: MOVEMENT_COLUMNS,
        rows: movements.map(m => ({
          Code: m.accountCode, Name: m.accountName, Pre: m.preCloseBalance,
          Dr: m.closingDebit, Cr: m.closingCredit, Post: m.postCloseBalance,
        })),
        title: `Year-End Closing Movement — ${data.yearCode}`,
        subtitle: data.companyName,
      }, `YearEnd_Movement_${data.yearCode}`);
    } else {
      exportToExcel({
        sheetName: 'Carry-Forward',
        columns: CARRYFORWARD_COLUMNS,
        rows: carryForwards.map(c => ({
          Code: c.accountCode, Name: c.accountName, Dr: c.carriedDebit,
          Cr: c.carriedCredit, Prior: c.priorYearClosing, Diff: c.difference,
        })),
        title: `Year-End Carry-Forward — ${data.yearCode}`,
        subtitle: data.companyName,
      }, `YearEnd_CarryForward_${data.yearCode}`);
    }
  };

  const handleCsv = () => {
    if (!data) return;
    if (isMovementTab) {
      exportToCsvRows(MOVEMENT_COLUMNS, movements.map(m => ({
        Code: m.accountCode, Name: m.accountName, Pre: m.preCloseBalance,
        Dr: m.closingDebit, Cr: m.closingCredit, Post: m.postCloseBalance,
      })), `YearEnd_Movement_${data.yearCode}`);
    } else {
      exportToCsvRows(CARRYFORWARD_COLUMNS, carryForwards.map(c => ({
        Code: c.accountCode, Name: c.accountName, Dr: c.carriedDebit,
        Cr: c.carriedCredit, Prior: c.priorYearClosing, Diff: c.difference,
      })), `YearEnd_CarryForward_${data.yearCode}`);
    }
  };

  return (
    <div className="flex flex-col h-full gap-4 p-4 overflow-auto">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BookLock size={22} className="text-brand-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Year-End Closing Report</h1>
        </div>
        <ReportToolbar
          onExcelExport={handleExcel}
          onCsvExport={handleCsv}
          hasData={!!data}
          reportKey={REPORT_KEY}
          rowCount={isMovementTab ? movements.length : carryForwards.length}
          filterSummary={`yearCode=${yearCode}`}
        />
      </div>

      {/* Filter panel */}
      <div className={cn(CARD_CLS, 'p-4')}>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="w-56">
            <label className={LABEL_CLS}>Financial Year Code *</label>
            <input
              type="text"
              value={yearCode}
              onChange={e => setYearCode(e.target.value)}
              placeholder="e.g. FY2026"
              className={INPUT_CLS}
            />
          </div>
          <RunReportButton onClick={() => { void run(); }} isLoading={loading} disabled={!yearCode.trim()} />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {loading && !data && (
        <div className="flex-1 flex items-center justify-center"><Spinner size="lg" /></div>
      )}

      {!data && !loading && (
        <EmptyState message='Enter a financial year code (e.g. FY2026) and click "Run Report" to see the year-end run details.' />
      )}

      {data && (
        <div className="flex flex-col gap-4">
          {/* Header card */}
          <div className={cn(CARD_CLS, 'p-4')}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                  {data.companyName} · {data.yearCode}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Generated {fmtDate(data.generatedAt)}
                </div>
              </div>
              <Pill label={data.status} tone={STATUS_TONES[data.status] ?? 'grey'} />
            </div>

            {notRun ? (
              <div className="mt-3">
                <InfoBanner>
                  No year-end closing run exists for {data.yearCode}. The figures below are empty until the run is executed.
                </InfoBanner>
              </div>
            ) : (
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 text-sm">
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">P&amp;L Net Balance</div>
                  <div className="font-semibold tabular-nums text-gray-900 dark:text-white">
                    {data.pnlNetBalance === null ? <Dash /> : fmtAmtZero(data.pnlNetBalance)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Retained Earnings Transferred</div>
                  <div className="font-semibold tabular-nums text-gray-900 dark:text-white">
                    {data.retainedEarningsTransferred === null ? <Dash /> : fmtAmtZero(data.retainedEarningsTransferred)}
                  </div>
                  {data.retainedEarningsAccountCode && (
                    <div className="text-[10px] text-gray-400 dark:text-gray-500">
                      → {data.retainedEarningsAccountCode}{data.retainedEarningsAccountName ? ` ${data.retainedEarningsAccountName}` : ''}
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Executed</div>
                  <div className="text-gray-900 dark:text-white">
                    {data.executedByName ?? <Dash />}
                    {data.executedDate ? <span className="text-xs text-gray-500 dark:text-gray-400"> · {fmtDate(data.executedDate)}</span> : null}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Completed</div>
                  <div className="text-gray-900 dark:text-white">
                    {data.completedByName ?? <Dash />}
                    {data.completedDate ? <span className="text-xs text-gray-500 dark:text-gray-400"> · {fmtDate(data.completedDate)}</span> : null}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Closing Journal</div>
                  <div className="font-mono text-gray-900 dark:text-white">{data.closingJournalNumber ?? <Dash />}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Opening Journal</div>
                  <div className="font-mono text-gray-900 dark:text-white">{data.openingJournalNumber ?? <Dash />}</div>
                </div>
                {data.rolledBackDate && (
                  <div className="col-span-2">
                    <div className="text-xs text-gray-500 dark:text-gray-400">Rolled Back</div>
                    <div className="text-gray-900 dark:text-white">
                      {data.rolledBackByName ?? 'Unknown'} · {fmtDate(data.rolledBackDate)}
                    </div>
                  </div>
                )}
              </div>
            )}

            {data.rollbackReason && (
              <div className="mt-3">
                <WarnBanner>Rollback reason: {data.rollbackReason}</WarnBanner>
              </div>
            )}
            {data.validationErrors && (
              <div className="mt-3">
                <WarnBanner>Validation errors: {data.validationErrors}</WarnBanner>
              </div>
            )}
          </div>

          <TabBar
            tabs={[
              { key: 'movements', label: 'Closing Movement' },
              { key: 'carryforward', label: 'Carry-Forward' },
            ]}
            active={tab}
            onChange={setTab}
          />

          {isMovementTab ? (
            <div className={cn(CARD_CLS, 'flex flex-col min-h-0')}>
              <div className="overflow-auto">
                <table className="w-full text-sm border-collapse min-w-[900px]">
                  <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                    <tr>
                      <th className={TH_CLS}>Account</th>
                      <th className={TH_CLS}>Name</th>
                      <th className={TH_R_CLS}>Pre-Close</th>
                      <th className={TH_R_CLS}>Closing Dr</th>
                      <th className={TH_R_CLS}>Closing Cr</th>
                      <th className={TH_R_CLS}>Post-Close</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movements.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                          No closing movements — the year-end closing journal has no lines for this year.
                        </td>
                      </tr>
                    ) : movements.map(m => (
                      <tr key={m.accountId} className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-3 py-2 text-xs font-mono text-gray-600 dark:text-gray-400 whitespace-nowrap">{m.accountCode}</td>
                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{m.accountName}</td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmtZero(m.preCloseBalance)}</td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmtZero(m.closingDebit)}</td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmtZero(m.closingCredit)}</td>
                        <td className={cn(AMT_CLS, 'font-medium text-gray-900 dark:text-gray-100')}>{fmtAmtZero(m.postCloseBalance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {cfBroken && (
                <WarnBanner>
                  Carry-forward difference detected — one or more accounts opened the new year with a balance
                  that does not match the prior-year closing balance.
                </WarnBanner>
              )}
              <div className={cn(CARD_CLS, 'flex flex-col min-h-0')}>
                <div className="overflow-auto">
                  <table className="w-full text-sm border-collapse min-w-[900px]">
                    <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                      <tr>
                        <th className={TH_CLS}>Account</th>
                        <th className={TH_CLS}>Name</th>
                        <th className={TH_R_CLS}>Carried Dr</th>
                        <th className={TH_R_CLS}>Carried Cr</th>
                        <th className={TH_R_CLS}>Prior-Year Closing</th>
                        <th className={TH_R_CLS}>Difference</th>
                      </tr>
                    </thead>
                    <tbody>
                      {carryForwards.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                            No carry-forward rows — the opening journal for the next year has not been generated.
                          </td>
                        </tr>
                      ) : carryForwards.map(c => {
                        const bad = hasCfDifference(c.difference);
                        return (
                          <tr key={c.accountId} className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                            <td className="px-3 py-2 text-xs font-mono text-gray-600 dark:text-gray-400 whitespace-nowrap">{c.accountCode}</td>
                            <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{c.accountName}</td>
                            <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmtZero(c.carriedDebit)}</td>
                            <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmtZero(c.carriedCredit)}</td>
                            <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmtZero(c.priorYearClosing)}</td>
                            <td className={cn(
                              AMT_CLS,
                              bad ? 'font-bold text-red-600 dark:text-red-400' : 'text-gray-400 dark:text-gray-600',
                            )}>
                              {fmtAmtZero(c.difference)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
